<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <h5 class="section-label mb-2 mt-0">
      {{ $t("Threshold Configuration") }}
    </h5>
    <validation-observer ref="thresholdConfigRules">
      <b-form @submit.prevent="handleSubmit">
        <b-row class="match-height">
          <b-col
            md="12"
            class="mt-0"
          >
            <div class="d-flex align-items-center justify-content-lg-end mb-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                style="display: flex"
                :disabled="isReadonly ? '' : isDisabled"
                @click="addMoreThresholdConfig"
              >
                <feather-icon
                  icon="PlusIcon"
                  class="mr-25"
                />
                <span> {{ $t("Add New") }} </span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <b-row
          v-for="(item, index) in thresholdList.data"
          :key="index"
          ref="thresholdRulesRef"
          class="mb-0"
        >
          <b-col md="12">
            <h5 class="section-label mb-1 mt-1">
              {{ item.type }} {{ $t("Section") }}
            </h5>
          </b-col>
          <b-col md="6">
            <SelectField
              id="type"
              v-model="item.type"
              :reduce="(option) => option"
              :label="$t('Type')"
              :placeholder="$t('Select')"
              :options="typesOptions"
              :error-name="$t('Type')"
              rules="required"
              item="label"
              :readonly="isReadonly"
              :set-selected="checkThresholdType"
            />
          </b-col>
          <b-col md="6">
            <InputNumberField
              id="amount"
              v-model="item.amount"
              :label="$t('Amount')"
              :placeholder="$t('Enter amount')"
              type="number"
              :error-name="$t('Amount')"
              rules="required"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="12">
            <TextAreaField
              id="notification-email"
              v-model="item.notificationEmails"
              :label="$t('Notification Emails (separate with comma)')"
              :placeholder="$t('Enter Notification Emails')"
              :error-name="$t('Notification Emails')"
              rules="required"
              rows="2"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col
            md="12"
            class="d-flex justify-content-end mt-2"
          >
            <Button
              :label="$t('Remove')"
              size="sm"
              icon="XIcon"
              show-icon
              :disabled="isReadonly ? '' : isDisabled"
              @click="removeThresholdConfigItem(index)"
            />
          </b-col>
        </b-row>
        <div class="text-center mt-3">
          <Button
            :label="buttonLabel"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="handleSubmit"
          />
        </div>
      </b-form>
    </validation-observer>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toBalance, toKobo } from '@/helpers/formatAmount'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import Button from '../../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BButton,
    ValidationObserver,
    InputNumberField,
    SelectField,
    TextAreaField,
    Button,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      sortBy: 'type',
      required,
      filterOn: [],
      filter: null,
      isDisabled: false,
      isReadonly: false,
      showSpinner: false,
      modalTitle: '',
      okTitle: this.$t('Update'),
      buttonLabel: this.$t('Edit'),
      thresholdList: {
        data: [],
      },
      typesOptions: ['BILLS', 'SMS', 'KYC'],
    }
  },
  mounted() {
    this.fetchThresholdConfiguration()
  },
  methods: {
    ...mapActions({
      getThresholdConfig: 'operations/getThresholdConfigList',
      updateThresholdConfiguration: 'operations/updateThresholdConfiguration',
    }),
    addMoreThresholdConfig() {
      this.thresholdList.data.push({
        type: '',
        amount: 0,
        notificationEmails: '',
      })
      this.$nextTick(() => {
        this.trAddHeight(this.$refs.thresholdRulesRef[0].offsetHeight)
      })
    },
    removeThresholdConfigItem(index) {
      this.thresholdList.data.splice(index, 1)
      this.trTrimHeight(this.$refs.thresholdRulesRef[0].offsetHeight)
    },
    checkThresholdType(type) {
      this.thresholdList.data.forEach(item => {
        if (item.type === type && Number(item.amount) > 0) {
          const text1 = this.$t('Threshold configuration type for')
          const text2 = this.$t('already exists')
          this.alertError({ text: `${text1} ${item.type} ${text2}` })
          this.thresholdList.data.pop()
        }
      })
    },
    async fetchThresholdConfiguration() {
      try {
        this.isBusy = true
        this.showSpinner = true
        this.isReadonly = true
        const response = await this.getThresholdConfig()
        const { status, data } = response
        if (status === 200 && data.data.length !== 0) {
          this.thresholdList.data = data.data.map(item => ({
            ...item,
            amount: item.amount ? toBalance(item.amount) : 0,
          }))
        } else {
          this.thresholdList.data.push({
            type: '',
            amount: 0,
            notificationEmails: '',
          })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const yourRequest = this.$t(
            'Your request to fetch Threshold Configuration failed. Reason:',
          )
          this.alertError({
            text: `${yourRequest} ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isBusy = false
        this.isReadonly = true
        this.showSpinner = false
      }
    },
    handleSubmit() {
      if (this.buttonLabel === this.$t('Save Changes')) {
        this.$refs.thresholdConfigRules.validate().then(success => {
          if (success) {
            const areYouSure = this.$t('Are you sure you want to')
            const thisText = this.$t('this Threshold Configuration?')
            this.confirm(this.thresholdConfigAction, {
              text: `${areYouSure} ${this.buttonLabel} ${thisText}`,
            })
          }
        })
      } else {
        this.isReadonly = false
        this.buttonLabel = this.$t('Save Changes')
      }
    },
    async thresholdConfigAction() {
      try {
        this.isLoading = true
        this.showSpinner = true
        const config = this.thresholdList.data.map(item => ({
          ...item,
          amount: item.amount ? toKobo(item.amount) : 0,
        }))
        const response = await this.updateThresholdConfiguration({
          config,
        })
        const { status } = response
        if (status === 200) {
          this.fetchThresholdConfiguration()
          this.isDisabled = false
          this.isLoading = false
          this.buttonLabel = this.$t('Edit')
          this.hideModal('modal-lg')
          this.alertSuccess({
            text: this.$t(
              'Your request to Modify Threshold Configuration was successful',
            ),
          })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        const reasonText = this.$t(
          'Your request to Modify Threshold Configuration failed. Reason:',
        )
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${reasonText} ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isLoading = false
        this.isDisabled = false
        this.showSpinner = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
